import React, { useEffect, useRef } from 'react'

import classNames from 'clsx'
import PropTypes from 'prop-types'

import { makeStyles } from '@mui/styles'

import getGlobal from '~/src/Lib/getGlobal'
import { EMPTY_OBJECT, noop, uniqueId } from '~/src/Lib/Utils'

const displayName = 'FadeBetween'

const useStyles = makeStyles({
  root: {
    display: 'contents',
  },
  both: {
    width: '100%',
    height: '100%',
    opacity: 0,
    transitionProperty: 'opacity',
  },
  in: {
    opacity: 1,
    pointerEvents: 'auto',
    transitionDelay: '0.1s',
    transitionDuration: '0.3s',
    zIndex: 2,
  },
  out: {
    position: 'absolute',
    pointerEvents: 'none',
    transitionDelay: '0s',
    transitionDuration: '0.2s',
    '& *': {
      pointerEvents: 'none',
    }
  },
}, { name: displayName })

/**
 * Fades between two visual states based on truthiness of a when prop
 * @component
 * @alias FadeBetween
 * @named_export FadeBetweenComponent
 */
export const FadeBetweenComponent = ({
  className = null,
  when,
  then = null,
  otherwise = null,
  onOtherwiseEntered = noop,
  onThenEntered = noop,
  onOtherwiseExited = noop,
  onThenExited = noop,
  ...props
}) => {
  const classes = useStyles(props)
  const root = useRef(null)
  const rootId = useRef(uniqueId('fade-between-'))

  useEffect(() => {
    const { current: rootElement } = root

    const handler = ({ propertyName, target }) => {
      const id = target.dataset.testid
      if (target.closest(`#${rootId.current}`) !== rootElement || !id || !id.startsWith('fade_between')) {
        return
      }

      const computed = getGlobal().getComputedStyle(target, null) ?? EMPTY_OBJECT
      const computedValue = Number(computed[propertyName]) || 0
      if (id.match(/otherwise/)) {
        (computedValue ? onOtherwiseEntered : onOtherwiseExited)({ root: rootElement, target })
      }
      if (id.match(/then/)) {
        (computedValue ? onThenEntered : onThenExited)({ root: rootElement, target })
      }
    }
    rootElement.addEventListener('transitionend', handler)

    return () => {
      if (!rootElement?.removeEventListener) {
        return
      }
      rootElement.removeEventListener('transitionend', handler)
    }
  }, [onOtherwiseEntered, onOtherwiseExited, onThenEntered, onThenExited])

  return (
    <div id={rootId.current} className={classNames(classes.root, className)} ref={root}>
      <div
        data-testid="fade_between-then"
        className={classNames(classes.both, when && classes.in, !when && classes.out)}
      >
        {then}
      </div>
      <div
        data-testid="fade_between-otherwise"
        className={classNames(classes.both, !when && classes.in, when && classes.out)}
      >
        {otherwise}
      </div>
    </div>
  )
}
FadeBetweenComponent.propTypes = {
  when: PropTypes.bool.isRequired,
  then: PropTypes.node,
  otherwise: PropTypes.node,
  onThenEntered: PropTypes.func,
  onOtherwiseEntered: PropTypes.func,
  onThenExited: PropTypes.func,
  onOtherwiseExited: PropTypes.func,
}
FadeBetweenComponent.displayName = displayName

export default React.memo(FadeBetweenComponent)
